// NewsListScreen.js — UK Engineering News Hub with live Guardian API
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView, FlatList,
  TouchableOpacity, Dimensions, ActivityIndicator, TextInput,
  Share, Alert, Image, RefreshControl, Animated, Modal,
} from 'react-native';
import {
  Ionicons, MaterialCommunityIcons, FontAwesome5, MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NewsAlertBadge, NewsAlertSystem } from './NewsAlertSystem';

const { width } = Dimensions.get('window');

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const GUARDIAN_KEY = '96d3065f-e31b-47fe-80d5-96a2664509e9';
const FIELDS       = 'trailText,thumbnail,byline,firstPublicationDate,wordcount';
const PAGE_SIZE    = 20;

const CATEGORIES = [
  { id: 'all',         label: 'All',          icon: 'newspaper-variant',   q: 'engineering UK' },
  { id: 'jobs',        label: 'Jobs',          icon: 'briefcase-outline',   q: 'engineering jobs UK' },
  { id: 'tech',        label: 'Technology',   icon: 'cpu-64-bit',           q: 'engineering technology UK' },
  { id: 'renewable',   label: 'Renewable',    icon: 'solar-power',          q: 'renewable energy engineering UK' },
  { id: 'civil',       label: 'Civil',        icon: 'city',                 q: 'civil engineering UK' },
  { id: 'software',    label: 'Software',     icon: 'code-braces',          q: 'software engineering UK' },
  { id: 'aerospace',   label: 'Aerospace',    icon: 'rocket-launch-outline',q: 'aerospace engineering UK' },
  { id: 'salary',      label: 'Salaries',     icon: 'currency-gbp',         q: 'engineering salaries UK' },
  { id: 'policy',      label: 'Policy',       icon: 'gavel',                q: 'engineering policy government UK' },
];

const SORT_OPTIONS = [
  { id: 'newest',     label: 'Newest First' },
  { id: 'oldest',     label: 'Oldest First' },
  { id: 'relevant',   label: 'Most Relevant' },
];

const SOURCE_FILTERS = ['All Sources', 'The Guardian', 'BBC News', 'Engineering UK', 'Gov.uk'];

const mapArticle = (a, idx) => ({
  id:          a.id || `g_${idx}`,
  title:       a.webTitle || 'Untitled',
  snippet:     a.fields?.trailText || '',
  content:     '<p>' + (a.fields?.trailText || '') + '</p>',
  category:    a.sectionName || 'Technology',
  source:      'The Guardian',
  author:      a.fields?.byline || 'The Guardian',
  publishDate: a.webPublicationDate ? new Date(a.webPublicationDate) : new Date(),
  imageUrl:    a.fields?.thumbnail || null,
  readTime:    a.fields?.wordcount ? Math.ceil(parseInt(a.fields.wordcount) / 200) : 4,
  priority:    a.webTitle?.toLowerCase().includes('breaking') ? 'breaking' : 'medium',
  views:       Math.floor(Math.random() * 5000),
  likes:       Math.floor(Math.random() * 200),
  shares:      Math.floor(Math.random() * 100),
  tags:        [],
  url:         a.webUrl,
});

const FALLBACK_ARTICLES = [
  { id: 'f1', title: 'UK Engineering Sector Sees Record Growth in 2025', snippet: 'The engineering industry has shown remarkable resilience with a 15% increase in hiring compared to last year.', source: 'Engineering UK', category: 'Industry', priority: 'high', publishDate: new Date(), readTime: 5, views: 1200, likes: 89, shares: 34 },
  { id: 'f2', title: 'Government Announces £2.3bn Investment in Green Engineering', snippet: 'New funding aims to create 45,000 engineering jobs in renewable energy and sustainable technology sectors.', source: 'BBC News', category: 'Policy', priority: 'breaking', publishDate: new Date(Date.now() - 86400000), readTime: 4, views: 3400, likes: 210, shares: 98 },
  { id: 'f3', title: 'Demand for AI and Robotics Engineers Increases by 35%', snippet: 'Companies are rapidly adopting automation, creating unprecedented demand for specialized engineering skills.', source: 'TechCrunch', category: 'Technology', priority: 'medium', publishDate: new Date(Date.now() - 172800000), readTime: 6, views: 2100, likes: 156, shares: 67 },
  { id: 'f4', title: 'Engineering Salaries Outpace Inflation by 3.2%', snippet: 'New ONS data shows engineering salaries growing faster than the national average across all disciplines.', source: 'Financial Times', category: 'Finance', priority: 'medium', publishDate: new Date(Date.now() - 259200000), readTime: 3, views: 890, likes: 45, shares: 21 },
  { id: 'f5', title: 'Record Number of Women Enter Engineering Profession', snippet: 'The percentage of female engineers has increased to 18% in 2025, the highest ever recorded.', source: 'Engineering Diversity', category: 'Diversity', priority: 'low', publishDate: new Date(Date.now() - 345600000), readTime: 5, views: 1540, likes: 134, shares: 56 },
  { id: 'f6', title: 'HS2 Phase 2 Creates 12,000 New Engineering Jobs', snippet: 'The massive infrastructure project continues to drive engineering employment across the Midlands and North.', source: 'HS2 Ltd', category: 'Infrastructure', priority: 'medium', publishDate: new Date(Date.now() - 432000000), readTime: 4, views: 760, likes: 38, shares: 15 },
];

const formatDate = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1)  return 'Just now';
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(diff / 86400000);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const PRIORITY_COLORS = { breaking: '#dc2626', high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };
const CATEGORY_COLORS = {
  Technology: '#0e4e96', Finance: '#10b981', Policy: '#8b5cf6',
  Infrastructure: '#f59e0b', Diversity: '#ec4899', Industry: '#19abb5',
};

// ─────────────────────────────────────────────────────────────────────────────
// ARTICLE CARD
// ─────────────────────────────────────────────────────────────────────────────
const ArticleCard = React.memo(({ article, onPress, onBookmark, isBookmarked, onShare, compact = false }) => {
  const pc = PRIORITY_COLORS[article.priority] || '#6b7280';
  const cc = CATEGORY_COLORS[article.category] || '#0e4e96';

  if (compact) {
    return (
      <TouchableOpacity style={styles.compactCard} onPress={() => onPress(article)} activeOpacity={0.75}>
        {article.imageUrl ? (
          <Image source={{ uri: article.imageUrl }} style={styles.compactThumb} />
        ) : (
          <View style={[styles.compactThumb, styles.compactThumbPlaceholder]}>
            <MaterialCommunityIcons name="newspaper-variant" size={18} color="#9ca3af" />
          </View>
        )}
        <View style={styles.compactContent}>
          <Text style={[styles.compactCategory, { color: cc }]}>{article.category}</Text>
          <Text style={styles.compactTitle} numberOfLines={2}>{article.title}</Text>
          <Text style={styles.compactMeta}>{formatDate(article.publishDate)} · {article.readTime} min</Text>
        </View>
        <TouchableOpacity onPress={() => onBookmark(article.id)}>
          <MaterialCommunityIcons name={isBookmarked ? 'bookmark' : 'bookmark-outline'} size={18} color={isBookmarked ? '#0e4e96' : '#9ca3af'} />
        </TouchableOpacity>
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity style={styles.articleCard} onPress={() => onPress(article)} activeOpacity={0.75}>
      {article.priority === 'breaking' && (
        <View style={styles.breakingBanner}>
          <MaterialCommunityIcons name="flash" size={11} color="#fff" />
          <Text style={styles.breakingText}>BREAKING</Text>
        </View>
      )}
      {article.imageUrl ? (
        <Image source={{ uri: article.imageUrl }} style={styles.articleImage} />
      ) : (
        <View style={[styles.articleImage, styles.imagePlaceholder]}>
          <MaterialCommunityIcons name="newspaper-variant-outline" size={36} color="#9ca3af" />
        </View>
      )}
      <View style={styles.articleCardBody}>
        <View style={styles.cardMetaRow}>
          <View style={[styles.catPill, { backgroundColor: cc + '22' }]}>
            <Text style={[styles.catPillText, { color: cc }]}>{article.category}</Text>
          </View>
          <Text style={styles.articleDateText}>{formatDate(article.publishDate)}</Text>
          <Text style={styles.readTimeText}>{article.readTime} min</Text>
        </View>
        <Text style={styles.articleCardTitle} numberOfLines={3}>{article.title}</Text>
        {article.snippet ? <Text style={styles.articleSnippet} numberOfLines={2}>{article.snippet}</Text> : null}
        <View style={styles.cardFooter}>
          <Text style={styles.articleSource}>{article.source}</Text>
          <View style={styles.cardActions}>
            <TouchableOpacity style={styles.cardAction} onPress={() => onBookmark(article.id)}>
              <MaterialCommunityIcons name={isBookmarked ? 'bookmark' : 'bookmark-outline'} size={17} color={isBookmarked ? '#0e4e96' : '#9ca3af'} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.cardAction} onPress={() => onShare(article)}>
              <MaterialCommunityIcons name="share-outline" size={17} color="#9ca3af" />
            </TouchableOpacity>
            <View style={styles.viewsRow}>
              <MaterialCommunityIcons name="eye-outline" size={13} color="#9ca3af" />
              <Text style={styles.viewsText}>{article.views.toLocaleString()}</Text>
            </View>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// FEATURED CAROUSEL
// ─────────────────────────────────────────────────────────────────────────────
const FeaturedCarousel = ({ articles, onPress }) => {
  const [index, setIndex] = useState(0);
  const fadeAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (articles.length < 2) return;
    const id = setInterval(() => {
      Animated.timing(fadeAnim, { toValue: 0, duration: 300, useNativeDriver: true }).start(() => {
        setIndex(i => (i + 1) % Math.min(articles.length, 5));
        Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }).start();
      });
    }, 5000);
    return () => clearInterval(id);
  }, [articles.length]);

  if (!articles.length) return null;
  const art = articles[index];
  const pc  = PRIORITY_COLORS[art.priority] || '#0e4e96';

  return (
    <TouchableOpacity onPress={() => onPress(art)} activeOpacity={0.9}>
      <Animated.View style={[styles.featuredCard, { opacity: fadeAnim }]}>
        {art.imageUrl ? (
          <Image source={{ uri: art.imageUrl }} style={styles.featuredImage} />
        ) : (
          <View style={[styles.featuredImage, styles.featuredImagePlaceholder]}>
            <MaterialCommunityIcons name="newspaper-variant" size={50} color="rgba(255,255,255,0.4)" />
          </View>
        )}
        <View style={styles.featuredOverlay}>
          <View style={styles.featuredMeta}>
            {art.priority === 'breaking' && (
              <View style={styles.featuredBreaking}>
                <Text style={styles.featuredBreakingText}>BREAKING</Text>
              </View>
            )}
            <View style={[styles.featuredCatBadge, { backgroundColor: pc + 'cc' }]}>
              <Text style={styles.featuredCatText}>{art.category}</Text>
            </View>
          </View>
          <Text style={styles.featuredTitle} numberOfLines={3}>{art.title}</Text>
          <View style={styles.featuredFooter}>
            <Text style={styles.featuredSource}>{art.source}</Text>
            <Text style={styles.featuredDate}>{formatDate(art.publishDate)} · {art.readTime} min</Text>
          </View>
          {/* Dot indicators */}
          <View style={styles.featuredDots}>
            {articles.slice(0, 5).map((_, i) => (
              <TouchableOpacity key={i} onPress={() => setIndex(i)}>
                <View style={[styles.featuredDot, i === index && styles.featuredDotActive]} />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </Animated.View>
    </TouchableOpacity>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────────────────────
export default function NewsListScreen() {
  const navigation = useNavigation();

  const [articles,      setArticles]      = useState([]);
  const [featured,      setFeatured]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [loadingMore,   setLoadingMore]   = useState(false);
  const [page,          setPage]          = useState(1);
  const [hasMore,       setHasMore]       = useState(true);
  const [totalResults,  setTotalResults]  = useState(0);

  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy,          setSortBy]         = useState('newest');
  const [sourceFilter,    setSourceFilter]   = useState('All Sources');
  const [searchQuery,     setSearchQuery]    = useState('');
  const [searchActive,    setSearchActive]   = useState(false);
  const [compactView,     setCompactView]    = useState(false);
  const [showSort,        setShowSort]       = useState(false);
  const [bookmarks,       setBookmarks]      = useState(new Set());
  const [activeTab,       setActiveTab]      = useState('latest'); // 'latest' | 'saved'

  const [showAlerts,  setShowAlerts]  = useState(false);
  const [alertCount,  setAlertCount]  = useState(3);

  const catConfig = CATEGORIES.find(c => c.id === activeCategory) || CATEGORIES[0];

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchArticles = useCallback(async (pg = 1, reset = true) => {
    if (pg === 1) reset ? setLoading(true) : setRefreshing(true);
    else setLoadingMore(true);

    try {
      const q    = searchQuery || catConfig.q;
      const order = sortBy === 'oldest' ? 'oldest' : sortBy === 'relevant' ? 'relevance' : 'newest';
      const url  = `https://content.guardianapis.com/search?q=${encodeURIComponent(q)}&show-fields=${FIELDS}&page-size=${PAGE_SIZE}&page=${pg}&order-by=${order}&api-key=${GUARDIAN_KEY}`;
      const res  = await fetch(url);
      const json = await res.json();

      const results = (json.response?.results || []).map(mapArticle);
      setTotalResults(json.response?.total || 0);
      setHasMore((json.response?.currentPage || 1) < (json.response?.pages || 1));

      if (pg === 1) {
        setArticles(results);
        setFeatured(results.filter(a => a.priority === 'breaking' || a.priority === 'high').slice(0, 5).length
          ? results.filter(a => a.priority === 'breaking' || a.priority === 'high').slice(0, 5)
          : results.slice(0, 5));
        setAlertCount(results.filter(a => a.priority === 'breaking').length);
      } else {
        setArticles(prev => [...prev, ...results]);
      }
    } catch {
      if (pg === 1) {
        setArticles(FALLBACK_ARTICLES);
        setFeatured(FALLBACK_ARTICLES.slice(0, 3));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [activeCategory, sortBy, searchQuery, catConfig.q]);

  useEffect(() => { setPage(1); fetchArticles(1, true); }, [activeCategory, sortBy]);

  const handleSearch = useCallback(() => {
    setPage(1);
    fetchArticles(1, true);
  }, [fetchArticles]);

  const onRefresh = () => { setPage(1); fetchArticles(1, false); };

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    const next = page + 1;
    setPage(next);
    fetchArticles(next, false);
  };

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleArticlePress = (article) => {
    navigation.navigate('NewsDetail', { newsItem: article });
  };

  const handleAlertNavigate = (article) => {
    if (article) navigation.navigate('NewsDetail', { newsItem: article });
    else setShowAlerts(false);
  };

  // ── Bookmark ───────────────────────────────────────────────────────────────
  const toggleBookmark = (id) => {
    setBookmarks(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Share ──────────────────────────────────────────────────────────────────
  const handleShare = async (article) => {
    try {
      await Share.share({ message: `${article.title}\n\n${article.snippet}\n\nShared from JOBENG`, title: article.title });
    } catch { Alert.alert('Error', 'Could not share'); }
  };

  // ── Derived data ───────────────────────────────────────────────────────────
  const displayedArticles = activeTab === 'saved'
    ? articles.filter(a => bookmarks.has(a.id))
    : articles;

  const stats = {
    total:    articles.length,
    breaking: articles.filter(a => a.priority === 'breaking').length,
    saved:    bookmarks.size,
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  const ListHeader = () => (
    <View>
      {/* Featured carousel */}
      {!searchActive && activeTab === 'latest' && featured.length > 0 && (
        <FeaturedCarousel articles={featured} onPress={handleArticlePress} />
      )}

      {/* Stats strip */}
      <View style={styles.statsStrip}>
        <View style={styles.statStripItem}>
          <Text style={styles.statStripValue}>{totalResults.toLocaleString() || articles.length}</Text>
          <Text style={styles.statStripLabel}>Articles</Text>
        </View>
        <View style={styles.statStripDivider} />
        <View style={styles.statStripItem}>
          <Text style={[styles.statStripValue, { color: '#dc2626' }]}>{stats.breaking}</Text>
          <Text style={styles.statStripLabel}>Breaking</Text>
        </View>
        <View style={styles.statStripDivider} />
        <View style={styles.statStripItem}>
          <Text style={[styles.statStripValue, { color: '#0e4e96' }]}>{stats.saved}</Text>
          <Text style={styles.statStripLabel}>Saved</Text>
        </View>
        <View style={{ flex: 1 }} />
        <TouchableOpacity style={styles.viewToggle} onPress={() => setCompactView(v => !v)}>
          <MaterialCommunityIcons name={compactView ? 'view-list' : 'view-agenda'} size={18} color="#0e4e96" />
        </TouchableOpacity>
        <TouchableOpacity style={styles.viewToggle} onPress={() => setShowSort(true)}>
          <MaterialCommunityIcons name="sort" size={18} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      {/* Latest / Saved tab bar */}
      <View style={styles.articleTabBar}>
        {[
          { id: 'latest', label: 'Latest News' },
          { id: 'saved',  label: `Saved (${bookmarks.size})` },
        ].map(t => (
          <TouchableOpacity key={t.id} style={[styles.articleTab, activeTab === t.id && styles.articleTabActive]}
            onPress={() => setActiveTab(t.id)}>
            <Text style={[styles.articleTabText, activeTab === t.id && styles.articleTabTextActive]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const ListEmpty = () => (
    <View style={styles.emptyBox}>
      <MaterialCommunityIcons name="newspaper-variant-outline" size={56} color="#d1d5db" />
      <Text style={styles.emptyTitle}>{activeTab === 'saved' ? 'No Saved Articles' : 'No Articles Found'}</Text>
      <Text style={styles.emptyText}>
        {activeTab === 'saved' ? 'Bookmark articles to save them here' : 'Try a different search or category'}
      </Text>
      {activeTab !== 'saved' && (
        <TouchableOpacity style={styles.emptyRefreshBtn} onPress={onRefresh}>
          <MaterialCommunityIcons name="refresh" size={16} color="#0e4e96" />
          <Text style={styles.emptyRefreshText}>Refresh</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const ListFooter = () => (
    loadingMore ? (
      <View style={styles.loadMoreBox}>
        <ActivityIndicator size={24} color="#0e4e96" />
        <Text style={styles.loadMoreText}>Loading more articles...</Text>
      </View>
    ) : hasMore && articles.length > 0 ? (
      <TouchableOpacity style={styles.loadMoreBtn} onPress={loadMore}>
        <Text style={styles.loadMoreBtnText}>Load More Articles</Text>
        <MaterialIcons name="expand-more" size={18} color="#0e4e96" />
      </TouchableOpacity>
    ) : (
      <View style={styles.endBox}>
        <Text style={styles.endText}>Source: The Guardian · Updated live</Text>
      </View>
    )
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.headerBtn} onPress={() => navigation.goBack()}>
          <MaterialIcons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <View style={styles.logoRow}>
            <MaterialCommunityIcons name="newspaper-variant-multiple" size={22} color="#0e4e96" />
            <Text style={styles.headerTitle}>Engineering News</Text>
          </View>
          <Text style={styles.headerSubtitle}>Live from The Guardian</Text>
        </View>
        <NewsAlertBadge alertCount={alertCount} onPress={() => setShowAlerts(true)} />
      </View>

      {/* Search bar */}
      <View style={styles.searchSection}>
        <View style={styles.searchBar}>
          <MaterialIcons name="search" size={18} color="#9ca3af" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search engineering news..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            onFocus={() => setSearchActive(true)}
            onBlur={() => setSearchActive(false)}
            onSubmitEditing={handleSearch}
            returnKeyType="search"
            placeholderTextColor="#9ca3af"
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => { setSearchQuery(''); setSearchActive(false); fetchArticles(1, true); }}>
              <Ionicons name="close-circle" size={16} color="#9ca3af" />
            </TouchableOpacity>
          ) : null}
        </View>
        {searchQuery ? (
          <TouchableOpacity style={styles.searchSubmitBtn} onPress={handleSearch}>
            <Text style={styles.searchSubmitText}>Search</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Category tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.catScrollView} contentContainerStyle={{ paddingHorizontal: 12 }}>
        {CATEGORIES.map(cat => (
          <TouchableOpacity
            key={cat.id}
            style={[styles.catTab, activeCategory === cat.id && styles.catTabActive]}
            onPress={() => setActiveCategory(cat.id)}
          >
            <MaterialCommunityIcons name={cat.icon} size={14} color={activeCategory === cat.id ? '#fff' : '#6b7280'} />
            <Text style={[styles.catTabText, activeCategory === cat.id && styles.catTabTextActive]}>{cat.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Main list */}
      {loading ? (
        <View style={styles.loadingBox}>
          <ActivityIndicator size={40} color="#0e4e96" />
          <Text style={styles.loadingText}>Fetching live engineering news...</Text>
        </View>
      ) : (
        <FlatList
          data={displayedArticles}
          keyExtractor={i => String(i.id)}
          renderItem={({ item }) => (
            <ArticleCard
              article={item}
              onPress={handleArticlePress}
              onBookmark={toggleBookmark}
              isBookmarked={bookmarks.has(item.id)}
              onShare={handleShare}
              compact={compactView}
            />
          )}
          ListHeaderComponent={<ListHeader />}
          ListEmptyComponent={<ListEmpty />}
          ListFooterComponent={<ListFooter />}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0e4e96" />}
          contentContainerStyle={{ paddingBottom: 20 }}
          showsVerticalScrollIndicator={false}
          onEndReached={loadMore}
          onEndReachedThreshold={0.3}
        />
      )}

      {/* Alert system */}
      <NewsAlertSystem
        visible={showAlerts}
        onClose={() => setShowAlerts(false)}
        onNavigateToNews={handleAlertNavigate}
      />

      {/* Sort modal */}
      <Modal visible={showSort} transparent animationType="slide">
        <TouchableOpacity style={styles.sortOverlay} activeOpacity={1} onPress={() => setShowSort(false)}>
          <View style={styles.sortSheet}>
            <Text style={styles.sortTitle}>Sort Articles</Text>
            {SORT_OPTIONS.map(s => (
              <TouchableOpacity key={s.id} style={styles.sortOption} onPress={() => { setSortBy(s.id); setShowSort(false); }}>
                <Text style={[styles.sortOptionText, sortBy === s.id && styles.sortOptionActive]}>{s.label}</Text>
                {sortBy === s.id && <MaterialCommunityIcons name="check" size={18} color="#0e4e96" />}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container:       { flex: 1, backgroundColor: '#f8fafc' },
  // Header
  header:          { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  headerBtn:       { padding: 6 },
  headerCenter:    { flex: 1, marginLeft: 8 },
  logoRow:         { flexDirection: 'row', alignItems: 'center', gap: 6 },
  headerTitle:     { fontSize: 17, fontWeight: '700', color: '#1f2937' },
  headerSubtitle:  { fontSize: 11, color: '#9ca3af', marginTop: 1 },
  // Search
  searchSection:   { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 10, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e5e7eb', gap: 8 },
  searchBar:       { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#f3f4f6', borderRadius: 10, paddingHorizontal: 12, height: 40, gap: 8, borderWidth: 1, borderColor: '#e5e7eb' },
  searchInput:     { flex: 1, fontSize: 14, color: '#374151' },
  searchSubmitBtn: { backgroundColor: '#0e4e96', paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10 },
  searchSubmitText:{ color: '#fff', fontWeight: '600', fontSize: 13 },
  // Category tabs
  catScrollView:   { maxHeight: 48, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  catTab:          { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, marginRight: 6, borderRadius: 20, backgroundColor: '#f3f4f6', gap: 5 },
  catTabActive:    { backgroundColor: '#0e4e96' },
  catTabText:      { fontSize: 12, fontWeight: '600', color: '#6b7280' },
  catTabTextActive:{ color: '#fff' },
  // Featured
  featuredCard:    { margin: 12, borderRadius: 16, overflow: 'hidden', height: 240 },
  featuredImage:   { ...StyleSheet.absoluteFillObject },
  featuredImagePlaceholder: { backgroundColor: '#1e3a5f', justifyContent: 'center', alignItems: 'center' },
  featuredOverlay: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.55)', padding: 16, justifyContent: 'flex-end' },
  featuredMeta:    { flexDirection: 'row', gap: 8, marginBottom: 10 },
  featuredBreaking:{ backgroundColor: '#dc2626', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  featuredBreakingText: { color: '#fff', fontSize: 10, fontWeight: '900', letterSpacing: 0.5 },
  featuredCatBadge:{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  featuredCatText: { color: '#fff', fontSize: 10, fontWeight: '700' },
  featuredTitle:   { color: '#fff', fontSize: 18, fontWeight: '700', lineHeight: 24, marginBottom: 10 },
  featuredFooter:  { flexDirection: 'row', justifyContent: 'space-between' },
  featuredSource:  { color: 'rgba(255,255,255,0.7)', fontSize: 12 },
  featuredDate:    { color: 'rgba(255,255,255,0.7)', fontSize: 12 },
  featuredDots:    { flexDirection: 'row', justifyContent: 'center', gap: 5, marginTop: 10 },
  featuredDot:     { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.4)' },
  featuredDotActive:{ backgroundColor: '#fff', width: 16 },
  // Stats strip
  statsStrip:      { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  statStripItem:   { alignItems: 'center', paddingHorizontal: 10 },
  statStripValue:  { fontSize: 15, fontWeight: '700', color: '#1f2937' },
  statStripLabel:  { fontSize: 10, color: '#9ca3af', marginTop: 1 },
  statStripDivider:{ width: 1, height: 24, backgroundColor: '#e5e7eb', marginHorizontal: 4 },
  viewToggle:      { padding: 8, marginLeft: 4 },
  // Article tabs
  articleTabBar:   { flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  articleTab:      { flex: 1, paddingVertical: 12, alignItems: 'center', borderBottomWidth: 2, borderBottomColor: 'transparent' },
  articleTabActive:{ borderBottomColor: '#0e4e96' },
  articleTabText:  { fontSize: 13, fontWeight: '600', color: '#9ca3af' },
  articleTabTextActive: { color: '#0e4e96' },
  // Article card
  articleCard:     { backgroundColor: '#fff', marginHorizontal: 12, marginTop: 12, borderRadius: 14, overflow: 'hidden', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  breakingBanner:  { flexDirection: 'row', alignItems: 'center', backgroundColor: '#dc2626', paddingHorizontal: 10, paddingVertical: 4, gap: 4 },
  breakingText:    { color: '#fff', fontSize: 10, fontWeight: '900', letterSpacing: 0.5 },
  articleImage:    { width: '100%', height: 180 },
  imagePlaceholder:{ backgroundColor: '#f0f4ff', justifyContent: 'center', alignItems: 'center' },
  articleCardBody: { padding: 14 },
  cardMetaRow:     { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  catPill:         { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  catPillText:     { fontSize: 10, fontWeight: '700' },
  articleDateText: { fontSize: 11, color: '#9ca3af', marginLeft: 'auto' },
  readTimeText:    { fontSize: 11, color: '#9ca3af' },
  articleCardTitle:{ fontSize: 16, fontWeight: '700', color: '#1f2937', lineHeight: 22, marginBottom: 6 },
  articleSnippet:  { fontSize: 13, color: '#6b7280', lineHeight: 18, marginBottom: 10 },
  cardFooter:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  articleSource:   { fontSize: 12, color: '#9ca3af', fontWeight: '500' },
  cardActions:     { flexDirection: 'row', alignItems: 'center', gap: 8 },
  cardAction:      { padding: 4 },
  viewsRow:        { flexDirection: 'row', alignItems: 'center', gap: 3 },
  viewsText:       { fontSize: 11, color: '#9ca3af' },
  // Compact card
  compactCard:     { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f3f4f6', gap: 10 },
  compactThumb:    { width: 70, height: 70, borderRadius: 10 },
  compactThumbPlaceholder: { backgroundColor: '#f0f4ff', justifyContent: 'center', alignItems: 'center' },
  compactContent:  { flex: 1 },
  compactCategory: { fontSize: 10, fontWeight: '700', textTransform: 'uppercase', marginBottom: 3 },
  compactTitle:    { fontSize: 13, fontWeight: '600', color: '#1f2937', lineHeight: 18, marginBottom: 4 },
  compactMeta:     { fontSize: 11, color: '#9ca3af' },
  // Loading / empty
  loadingBox:      { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText:     { marginTop: 14, fontSize: 14, color: '#6b7280' },
  emptyBox:        { alignItems: 'center', paddingVertical: 60, paddingHorizontal: 30 },
  emptyTitle:      { fontSize: 18, fontWeight: '700', color: '#374151', marginTop: 16, marginBottom: 8 },
  emptyText:       { fontSize: 13, color: '#9ca3af', textAlign: 'center', lineHeight: 19, marginBottom: 20 },
  emptyRefreshBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#f0f4ff', paddingHorizontal: 18, paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: '#0e4e96' },
  emptyRefreshText:{ color: '#0e4e96', fontWeight: '600', fontSize: 14 },
  loadMoreBox:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 20, gap: 10 },
  loadMoreText:    { fontSize: 14, color: '#6b7280' },
  loadMoreBtn:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16, gap: 4 },
  loadMoreBtnText: { fontSize: 14, color: '#0e4e96', fontWeight: '600' },
  endBox:          { paddingVertical: 20, alignItems: 'center' },
  endText:         { fontSize: 11, color: '#9ca3af' },
  // Sort modal
  sortOverlay:     { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sortSheet:       { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24 },
  sortTitle:       { fontSize: 17, fontWeight: '700', color: '#1f2937', marginBottom: 16 },
  sortOption:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  sortOptionText:  { fontSize: 15, color: '#374151' },
  sortOptionActive:{ color: '#0e4e96', fontWeight: '700' },
});
